import { IconType } from "react-icons";
import { FaPhoneAlt, FaSms, FaWhatsapp } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import {
  IHistoricoNotificacaoCcbItem,
  NotificacaoEventoEnum,
} from "@/models/consignado/proposal-billings.model";

export type StepState = "completed" | "current" | "error" | "future";

export const STEP_COLORS: Record<StepState, { bg: string; color: string; line: string }> = {
  completed: { bg: "green.100", color: "green.400", line: "green.100" },
  current: { bg: "green.500", color: "white", line: "gray.300" },
  error: { bg: "red.500", color: "white", line: "gray.300" },
  future: { bg: "gray.100", color: "gray.400", line: "gray.300" },
};

export const STATE_LABEL: Record<StepState, string> = {
  completed: "Concluído",
  current: "Mais recente",
  error: "Erro no envio",
  future: "Pendente",
};

// Rótulo em duas linhas por evento (cabeçalho da coluna e título do popover).
// A chave é o `tipo` da notificação = `nome` gravado pelo módulo de
// notificação (mesmo valor de NotificacaoEventoEnum). Tipo fora da lista cai em
// `labelLinhasEvento`, que mostra o valor cru — não inventar rótulo.
export const EVENTO_LABEL_LINHAS: Record<string, [string, string]> = {
  [NotificacaoEventoEnum.SOLICITACAO_CONTATO_RH]: ["Contato", "RH"],
  [NotificacaoEventoEnum.SOLICITACAO_CONTATO_RH_PENDENTE]: ["Contato RH", "Pendente"],
  [NotificacaoEventoEnum.EMPRESTIMO_CONTRATADO_AVISO_TOMADOR]: ["Empréstimo", "Contratado"],
  [NotificacaoEventoEnum.APENAS_WHATSAPP_META]: ["WhatsApp", "Meta"],
  [NotificacaoEventoEnum.APENAS_EMAIL]: ["Apenas", "E-mail"],
  [NotificacaoEventoEnum.EMAIL_DIRETO]: ["E-mail", "Direto"],
  [NotificacaoEventoEnum.EMPRESTIMO_PAGO_EMAIL_EMPREGADO]: ["Empréstimo", "Pago"],
  [NotificacaoEventoEnum.EMPRESTIMO_PAGO_EMAIL_EMPREGADOR]: ["Empréstimo", "Pago (RH)"],
  [NotificacaoEventoEnum.EMPRESTIMO_PAGO_WHATSAPP_TOMADOR]: ["Empréstimo", "Pago (Wpp)"],
  [NotificacaoEventoEnum.PROPOSTA_EXPIRACAO_WHATSAPP_TOMADOR]: ["Expiração", "Proposta"],
  [NotificacaoEventoEnum.CODIGO_SEGURANCA_WHATSAPP]: ["Código", "Segurança"],
  [NotificacaoEventoEnum.COMPETENCIA_FASE1_RETENCAO_EMAIL_EMPREGADOR]: ["Retenção", "(Fase 1)"],
  [NotificacaoEventoEnum.COMPETENCIA_FASE2_ESCRITURACAO_EMAIL_EMPREGADOR]: ["Escrituração", "(Fase 2)"],
  [NotificacaoEventoEnum.COMPETENCIA_FASE3_ALERTA_ESCRITURACAO_EMAIL_EMPREGADOR]: ["Alerta", "Escrituração"],
  [NotificacaoEventoEnum.COMPETENCIA_FASE4_ALERTA_DAE_EMAIL_EMPREGADOR]: ["Alerta", "DAE"],
};

// O `nome` gravado pelo módulo de notificação vem em kebab-case minúsculo
// (`competencia-fase4-alerta-dae-email-empregador`, visto na resposta real);
// o enum do front é UPPER_SNAKE — normaliza os dois pro mesmo formato.
export const normalizarTipo = (tipo: string | null | undefined) =>
  (tipo ?? "").trim().toUpperCase().replace(/-/g, "_");

export function labelLinhasEvento(tipo: string): [string, string] {
  return EVENTO_LABEL_LINHAS[normalizarTipo(tipo)] ?? [tipo || "Evento", ""];
}

// Ícone pelo canal (EMAIL / SMS / WHATSAPP_*); o `tipo` entra como
// desempate pra notificação cujo canal veio fora do enum.
export function resolveChannelIcon(canal: string, tipo = ""): IconType {
  const chave = `${canal} ${tipo}`.toUpperCase();
  if (chave.includes("WHATSAPP")) return FaWhatsapp;
  if (chave.includes("SMS")) return FaSms;
  if (chave.includes("LIGACAO")) return FaPhoneAlt;
  return MdEmail;
}

// `status` é o estado bruto (PENDENTE | ENVIANDO | ENVIADO | FALHA); o backend
// resume em `status_envio` SUCCESS só pra ENVIADO. Pendente/enviando ainda não
// é erro pro operador, então fica cinza ("Pendente") em vez de vermelho.
export function resolveStepState(item: IHistoricoNotificacaoCcbItem, isLast: boolean): StepState {
  const status = (item.status ?? "").toUpperCase();
  if (status === "PENDENTE" || status === "ENVIANDO") return "future";
  if (item.status_envio === "ERROR") return "error";
  if (isLast) return "current";
  return "completed";
}

export const ehEmail = (destinatario: string) => destinatario.includes("@");
